import React, { useState } from 'react';
import { getRules, saveRule } from '../../services/storage';
import { THEME } from '../../styles/theme';

// ── Contradiction detector ────────────────────────────────────────────
// Finds rule pairs that directly conflict (e.g. one says ALWAYS BUY London
// and another says NEVER trade London).
function detectContradictions(rules) {
  const active = rules.filter(r => r.status === 'active');
  const conflicts = [];

  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i];
      const b = active[j];

      // Check for session conflicts
      const aText = `${a.title} ${a.description} ${a.action}`.toLowerCase();
      const bText = `${b.title} ${b.description} ${b.action}`.toLowerCase();

      const alwaysPairs = [
        ['always', 'never'],
        ['buy', 'sell'],
        ['enter', 'avoid'],
        ['trade', "don't trade"],
      ];

      for (const [termA, termB] of alwaysPairs) {
        if (aText.includes(termA) && bText.includes(termB) && shareKeyword(aText, bText)) {
          conflicts.push({ a, b, reason: `One says "${termA}", other says "${termB}" on same condition` });
          break;
        }
        if (aText.includes(termB) && bText.includes(termA) && shareKeyword(aText, bText)) {
          conflicts.push({ a, b, reason: `One says "${termB}", other says "${termA}" on same condition` });
          break;
        }
      }
    }
  }

  // Deduplicate by ID pair
  const seen = new Set();
  return conflicts.filter(c => {
    const key = [c.a.id, c.b.id].sort().join('-');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function shareKeyword(a, b) {
  const keywords = ['london', 'ny', 'asian', 'ob', 'order block', 'fvg', 'liquidity', 'bos', 'session', 'monday', 'friday'];
  return keywords.some(k => a.includes(k) && b.includes(k));
}

// ── Styles ────────────────────────────────────────────────────────────
const CONF_STYLES = {
  UNTESTED:     { color: THEME.colors.textMuted, bg: 'rgba(255,255,255,0.05)', label: 'UNTESTED' },
  MEDIUM:       { color: THEME.colors.gold,      bg: 'rgba(255,215,0,0.08)',   label: 'MEDIUM' },
  HIGH:         { color: THEME.colors.blue,       bg: 'rgba(0,212,255,0.08)',  label: 'HIGH' },
  under_review: { color: THEME.colors.red,        bg: 'rgba(255,51,51,0.08)',  label: 'REVIEW' },
};

const S = {
  page: { padding: '20px', maxWidth: '100%', paddingBottom: '40px' },
  header: { marginBottom: '20px' },
  title: { fontSize: '22px', fontWeight: '700', color: THEME.colors.blue, letterSpacing: '2px', fontFamily: THEME.fonts.hud },
  subtitle: { fontSize: '13px', color: THEME.colors.textMuted, marginTop: '4px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: (active) => ({
    padding: '7px 16px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
    border: active ? `1px solid ${THEME.colors.blue}` : '1px solid rgba(255,255,255,0.1)',
    background: active ? 'rgba(0,212,255,0.1)' : 'transparent',
    color: active ? THEME.colors.blue : THEME.colors.textMuted, transition: 'all 0.15s',
  }),
  card: {
    background: 'rgba(255,255,255,0.03)',
    borderTop: '1px solid rgba(255,255,255,0.07)',
    borderRight: '1px solid rgba(255,255,255,0.07)',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    borderLeft: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '12px', padding: '16px', marginBottom: '12px',
  },
  badge: (color, bg) => ({
    padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
    color, background: bg || `${color}18`, display: 'inline-block',
  }),
  input: {
    width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: THEME.colors.text, fontSize: '14px', padding: '9px 12px',
    outline: 'none', fontFamily: THEME.fonts.body, boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: THEME.colors.text, fontSize: '14px', padding: '9px 12px',
    resize: 'vertical', minHeight: '64px', outline: 'none', fontFamily: THEME.fonts.body,
    lineHeight: '1.6', boxSizing: 'border-box',
  },
  btn: (variant) => ({
    padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '700',
    background: variant === 'primary' ? THEME.colors.blue
              : variant === 'green'   ? 'rgba(0,255,136,0.12)'
              : variant === 'danger'  ? 'rgba(255,51,51,0.12)'
              : 'rgba(255,255,255,0.07)',
    color: variant === 'primary' ? '#000'
         : variant === 'green'   ? THEME.colors.green
         : variant === 'danger'  ? THEME.colors.red
         : THEME.colors.textMuted,
  }),
  winBar: (rate) => ({
    height: '3px',
    width: `${rate}%`,
    background: rate >= 65 ? THEME.colors.green : rate >= 45 ? THEME.colors.gold : THEME.colors.red,
    borderRadius: '2px', transition: 'width 0.5s',
  }),
  conflictCard: {
    background: 'rgba(255,51,51,0.06)', border: '1px solid rgba(255,51,51,0.2)',
    borderRadius: '10px', padding: '14px', marginBottom: '10px',
  },
  sectionLabel: {
    fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.textMuted,
    fontFamily: THEME.fonts.hud, marginBottom: '8px', textTransform: 'uppercase',
  },
};

// ── Add Rule Form ─────────────────────────────────────────────────────
function AddRuleForm({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', action: 'ALWAYS', description: '', reason: '',
    session: 'Any', ict_concept: '', rr_minimum: 2,
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.title.trim() || !form.description.trim()) return;
    saveRule({ ...form, source: 'Manual', status: 'active' });
    setForm({ title: '', action: 'ALWAYS', description: '', reason: '', session: 'Any', ict_concept: '', rr_minimum: 2 });
    setOpen(false);
    onSaved();
  };

  if (!open) return (
    <button style={{ ...S.btn('primary'), marginBottom: '16px' }} onClick={() => setOpen(true)}>
      + Add Rule
    </button>
  );

  return (
    <div style={{ ...S.card, borderColor: 'rgba(0,212,255,0.2)', marginBottom: '16px' }}>
      <div style={S.sectionLabel}>NEW TRADING RULE</div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <select style={{ ...S.input, flex: '0 0 120px' }} value={form.action} onChange={e => set('action', e.target.value)}>
          <option value="ALWAYS">ALWAYS</option>
          <option value="NEVER">NEVER</option>
          <option value="IF-THEN">IF-THEN</option>
          <option value="WAIT">WAIT</option>
        </select>
        <input style={{ ...S.input, flex: 1 }} placeholder="Rule title (e.g. Wait for BOS before entry)"
          value={form.title} onChange={e => set('title', e.target.value)} />
      </div>
      <textarea style={{ ...S.textarea, marginBottom: '8px' }}
        placeholder="Full rule description — be specific about conditions"
        value={form.description} onChange={e => set('description', e.target.value)} />
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <select style={{ ...S.input, flex: 1 }} value={form.session} onChange={e => set('session', e.target.value)}>
          {['Any','Pre-London','London','London+NY','NY Close','Asian'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input style={{ ...S.input, flex: 1 }} placeholder="ICT concept (OB, FVG, BOS...)"
          value={form.ict_concept} onChange={e => set('ict_concept', e.target.value)} />
        <input type="number" style={{ ...S.input, flex: '0 0 80px' }} placeholder="R:R" min="1" max="10" step="0.5"
          value={form.rr_minimum} onChange={e => set('rr_minimum', parseFloat(e.target.value))} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={S.btn('primary')} onClick={handleSave}>Save Rule</button>
        <button style={S.btn()} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

// ── Single Rule Card ──────────────────────────────────────────────────
function RuleCard({ rule, onRefresh }) {
  const conf = CONF_STYLES[rule.confidence] || CONF_STYLES.UNTESTED;
  const actionColor = rule.action === 'ALWAYS' ? THEME.colors.green
                    : rule.action === 'NEVER'  ? THEME.colors.red
                    : THEME.colors.gold;

  return (
    <div style={{ ...S.card, borderLeft: `3px solid ${actionColor}44` }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: '800', color: actionColor, fontFamily: THEME.fonts.hud }}>
              [{rule.id}] {rule.action}
            </span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: THEME.colors.text }}>{rule.title}</span>
          </div>
          {rule.description && (
            <div style={{ fontSize: '13px', color: THEME.colors.textMuted, lineHeight: '1.6' }}>{rule.description}</div>
          )}
        </div>
        <span style={S.badge(conf.color, conf.bg)}>{conf.label}</span>
      </div>

      {/* Win rate bar */}
      {rule.total_count > 0 && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '3px' }}>
            <span>Win Rate</span>
            <span style={{ color: rule.win_rate >= 60 ? THEME.colors.green : THEME.colors.red, fontWeight: '700' }}>
              {rule.win_rate}% ({rule.win_count}W/{rule.total_count - rule.win_count}L)
            </span>
          </div>
          <div style={{ height: '3px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={S.winBar(rule.win_rate)} />
          </div>
        </div>
      )}

      {/* Meta tags */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '11px', color: THEME.colors.textMuted }}>
        {rule.session && rule.session !== 'Any' && <span>🕐 {rule.session}</span>}
        {rule.ict_concept && <span>📐 {rule.ict_concept}</span>}
        {rule.rr_minimum > 0 && <span>📊 Min R:R {rule.rr_minimum}</span>}
        {rule.source && <span>📌 {rule.source}</span>}
      </div>
    </div>
  );
}

// ── Contradictions Panel ──────────────────────────────────────────────
function ContradictionsPanel({ rules }) {
  const conflicts = detectContradictions(rules);

  if (conflicts.length === 0) {
    return (
      <div style={{
        background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)',
        borderRadius: '10px', padding: '16px', marginBottom: '16px',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <span style={{ fontSize: '20px' }}>✅</span>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: THEME.colors.green }}>No contradictions found</div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted }}>All {rules.filter(r => r.status === 'active').length} active rules are consistent.</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{
        background: 'rgba(255,51,51,0.05)', border: '1px solid rgba(255,51,51,0.2)',
        borderRadius: '10px', padding: '12px 16px', marginBottom: '12px',
        fontSize: '13px', color: THEME.colors.red,
      }}>
        ⚠️ {conflicts.length} contradiction{conflicts.length > 1 ? 's' : ''} detected — review and resolve these rules.
      </div>
      {conflicts.map((c, i) => (
        <div key={i} style={S.conflictCard}>
          <div style={{ fontSize: '12px', color: THEME.colors.red, marginBottom: '8px', fontWeight: '700' }}>
            CONFLICT: {c.reason}
          </div>
          <div style={{ fontSize: '13px', color: THEME.colors.text, marginBottom: '4px' }}>
            [{c.a.id}] {c.a.title}
          </div>
          <div style={{ fontSize: '13px', color: THEME.colors.textMuted }}>vs</div>
          <div style={{ fontSize: '13px', color: THEME.colors.text, marginTop: '4px' }}>
            [{c.b.id}] {c.b.title}
          </div>
        </div>
      ))}
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────────
export default function RuleBook() {
  const [tab, setTab]     = useState('active');
  const [rules, setRules] = useState(() => getRules());
  const [search, setSearch] = useState('');

  const refresh = () => setRules(getRules());

  const active   = rules.filter(r => r.status === 'active');
  const retired  = rules.filter(r => r.status !== 'active');
  const filtered = (tab === 'active' ? active : tab === 'retired' ? retired : rules)
    .filter(r => !search || r.title.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase()));

  const tabs = [
    { id: 'active',   label: `Active (${active.length})` },
    { id: 'check',    label: '⚠️ Contradictions' },
    { id: 'retired',  label: `Retired (${retired.length})` },
    { id: 'all',      label: `All (${rules.length})` },
  ];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.title}>📋 RULE BOOK</div>
        <div style={S.subtitle}>{active.length} active rules · JARVIS applies these to every analysis</div>
      </div>

      <div style={S.tabs}>
        {tabs.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'check' ? (
        <ContradictionsPanel rules={rules} />
      ) : (
        <>
          <AddRuleForm onSaved={refresh} />
          <input
            style={{ ...S.input, marginBottom: '14px' }}
            placeholder="Search rules..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: THEME.colors.textMuted, fontSize: '14px' }}>
              {rules.length === 0
                ? 'No rules yet. Add rules manually or they are extracted automatically from daily recaps.'
                : 'No rules match this search.'}
            </div>
          )}
          {filtered.map(rule => (
            <RuleCard key={rule.id} rule={rule} onRefresh={refresh} />
          ))}
        </>
      )}
    </div>
  );
}

